import { useForm } from "react-hook-form";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { yupResolver } from '@hookform/resolvers/yup';
import i18next from 'i18next';
import Swal from "sweetalert2";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";

import { SuccessAlert } from "@shared/ui/alerts";
import { FormatNumberMoney } from "@shared/utils/moneyFormat";
import { sendInvoiceAutomatic } from "../api/invoiceApi";
import { chargedInvoiceAutomaticSchema } from "../schema/chargedInvoiceAutomaticSchema";
import { ChargedManual } from "./ChargedManual";

import type { SendInvoiceRequest } from "../api/invoiceApi";
import type { ChargedInvoiceAutomaticValues } from "../schema/chargedInvoiceAutomaticSchema";
import type { InvoiceRow } from "../lib/invoiceRow";

/** Campos de `userData` (hook legado `useUserProfile`, tipado `unknown`) que este componente lee. */
interface ChargedInvoiceAutomaticUserData {
    email?: string;
}

export interface ChargedInvoiceAutomaticDialogProps {
    handleClose: () => void;
    open: boolean;
    notLoading: () => void;
    loading: () => void;
    userData: ChargedInvoiceAutomaticUserData;
    invoiceId: number | null;
    invoices: InvoiceRow[];
    handleInvoices: (invoices: InvoiceRow[]) => void;
}

export const ChargedInvoiceAutomaticDialog = ({handleClose, open, notLoading, loading, userData, invoiceId, invoices, handleInvoices}: ChargedInvoiceAutomaticDialogProps) => {

    const {control, handleSubmit, reset, formState: {errors}} = useForm<ChargedInvoiceAutomaticValues>({
        resolver: yupResolver(chargedInvoiceAutomaticSchema) as never,
    });

    const onClose = () => {
        handleClose()
        reset()
    }

    const onRegister = (value: ChargedInvoiceAutomaticValues) => {
        loading()
        onClose()
        // `invoiceId` viene de `InvoicePage` (`useState<number | null>`), solo se setea antes de
        // abrir este diálogo: en el submit siempre está definido (comportamiento preexistente,
        // sin guard nuevo). El passthrough de `ccEmail` (`string | null` acá vs `string | undefined`
        // en el DTO) también es tal cual venía: no se normaliza `null` a `undefined` porque eso
        // cambiaría qué manda el JSON (una clave en `null` vs. una clave ausente).
        const payload = { ...value, invoiceId: invoiceId as number } as unknown as SendInvoiceRequest;
        const invoiceData = Array.isArray(invoices)
            ? invoices.find((item) => item.invoiceId === invoiceId || item.id === invoiceId)
            : null;
        sendInvoiceAutomatic(payload).then(() => {
            // Fila aplanada (ver `InvoiceRow`): el match usa `invoiceId`/`id`, igual que
            // `invoiceData` arriba, no un `.invoice` anidado que esta fila nunca tiene.
            const newInvoices = invoices.map((item) => item.invoiceId === invoiceId || item.id === invoiceId
                    ? { ...item, status: 'SENT' }
                    : item
            );
            handleInvoices(newInvoices)
            if (invoiceData && invoiceData.month && invoiceData.year) {
                const periodLabel = format(new Date(invoiceData.year, invoiceData.month - 1, 1), 'MMMM - yyyy', {
                    locale: i18next.language === "es" ? es : enUS,
                }).toUpperCase();
                const totalLabel = invoiceData.total != null && invoiceData.currency
                    ? FormatNumberMoney(invoiceData.currency).format(invoiceData.total)
                    : null;
                Swal.fire({
                    icon: 'success',
                    title: i18next.t("uploadInvoice"),
                    text: i18next.t("doneSuccessfully", {a: "a"}),
                    html: `<div>${i18next.t("period")}: <strong>${periodLabel}</strong></div>${
                        totalLabel ? `<div>${i18next.t("totalInvoice")}: <strong>${totalLabel}</strong></div>` : ""
                    }`,
                }).then()
            } else {
                SuccessAlert(i18next.t("uploadInvoice"), i18next.t("doneSuccessfully", {a: "a"})).then()
            }
        }).catch((err) => {
            console.error(err)
            // {response: {data}}
            // ErrorAlert("Error al Cargar Factura", data.message).then()
        }).finally(notLoading)
    }

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <form onSubmit={handleSubmit(onRegister)}>
                    <DialogTitle>{i18next.t("automaticInvoiceUpload")}</DialogTitle>
                    <DialogContent>
                        <Box sx={{ p: 1 }}>
                            <Grid container spacing={3}>
                                <ChargedManual errors={errors} control={control} defaultValue={userData}/>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>{i18next.t("cancel")}</Button>
                        <Button type="submit">{i18next.t("load")}</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}
